import { useQuery } from '@tanstack/react-query';
import {
  Users,
  DollarSign,
  Gift,
  Clock,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

interface MetricsOverview {
  users: {
    total: number;
    active30d: number;
  };
  referrals: {
    total: number;
    converted: number;
    conversionRate: string;
  };
  payouts: {
    pending: number;
    pendingAmount: number;
  };
  earnings: {
    total: number;
  };
}

interface TradingMetrics {
  period: string;
  trades: number;
  volume: number;
  uniqueTraders: number;
  avgTradeSize: string | number;
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  trend,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="p-4 border border-white/10 bg-black">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-zinc-500 mb-1">{label}</p>
          <p className="text-2xl font-mono font-medium text-white">{value}</p>
          {subValue && (
            <p className={`text-xs mt-1 ${
              trend === 'up' ? 'text-emerald-400' :
              trend === 'down' ? 'text-red-400' :
              'text-zinc-500'
            }`}>
              {subValue}
            </p>
          )}
        </div>
        <Icon className="w-5 h-5 text-zinc-600" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  // Fetch overview metrics
  const { data: overview, isLoading: isOverviewLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['admin-metrics-overview'],
    queryFn: () => apiRequest('GET', '/api/admin/metrics/overview') as Promise<MetricsOverview>,
  });

  // Fetch trading metrics
  const { data: trading, isLoading: isTradingLoading } = useQuery({
    queryKey: ['admin-metrics-trading'],
    queryFn: () => apiRequest('GET', '/api/admin/metrics/trading?days=30') as Promise<TradingMetrics>,
  });

  // Fetch referral metrics
  const { data: referralMetrics } = useQuery({
    queryKey: ['admin-metrics-referrals'],
    queryFn: () => apiRequest('GET', '/api/admin/metrics/referrals'),
  });

  const isLoading = isOverviewLoading || isTradingLoading;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Dashboard</h1>
          <p className="text-sm text-zinc-500">Overview of platform metrics</p>
        </div>
        <button
          onClick={() => refetchOverview()}
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 hover:text-white
            border border-white/10 hover:bg-white/5 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={Users}
              label="Total Users"
              value={overview?.users.total.toLocaleString() || 0}
              subValue={`${overview?.users.active30d || 0} active (30d)`}
            />
            <StatCard
              icon={Gift}
              label="Total Referrals"
              value={overview?.referrals.total || 0}
              subValue={`${overview?.referrals.conversionRate} conversion`}
              trend="up"
            />
            <StatCard
              icon={DollarSign}
              label="Referral Earnings"
              value={`$${(overview?.earnings.total || 0).toFixed(2)}`}
              subValue={`$${(overview?.payouts.pendingAmount || 0).toFixed(2)} pending`}
            />
            <StatCard
              icon={Clock}
              label="Pending Payouts"
              value={overview?.payouts.pending || 0}
              subValue="awaiting approval"
            />
          </div>

          {/* Trading Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Trading Volume */}
            <div className="border border-white/10 p-4">
              <h3 className="text-sm font-medium text-white mb-4">Trading (30 Days)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Total Volume</p>
                  <p className="text-xl font-mono text-white">
                    ${((trading?.volume || 0) / 1000).toFixed(1)}k
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Total Trades</p>
                  <p className="text-xl font-mono text-white">
                    {(trading?.trades || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Unique Traders</p>
                  <p className="text-xl font-mono text-white">
                    {trading?.uniqueTraders || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Avg Trade Size</p>
                  <p className="text-xl font-mono text-white">
                    ${trading?.avgTradeSize || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Top Referrers */}
            <div className="border border-white/10 p-4">
              <h3 className="text-sm font-medium text-white mb-4">Top Referrers</h3>
              {referralMetrics?.topReferrers?.length > 0 ? (
                <div className="space-y-2">
                  {referralMetrics.topReferrers.slice(0, 5).map((referrer: any, i: number) => (
                    <div key={referrer.user_id} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center text-xs bg-white/5 text-zinc-400">
                          {i + 1}
                        </span>
                        <span className="text-xs font-mono text-zinc-300">
                          {referrer.user_id.slice(0, 6)}...{referrer.user_id.slice(-4)}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-emerald-400">
                        ${parseFloat(referrer.total_earnings || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">No referrers yet</p>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="border border-white/10 p-4">
            <h3 className="text-sm font-medium text-white mb-4">Recent Referrals</h3>
            {referralMetrics?.recentReferrals?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-xs text-zinc-500 py-2">Referrer</th>
                      <th className="text-left text-xs text-zinc-500 py-2">Referred</th>
                      <th className="text-left text-xs text-zinc-500 py-2">Level</th>
                      <th className="text-left text-xs text-zinc-500 py-2">Status</th>
                      <th className="text-left text-xs text-zinc-500 py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralMetrics.recentReferrals.slice(0, 10).map((ref: any) => (
                      <tr key={ref.id} className="border-b border-white/5">
                        <td className="py-2 text-xs font-mono text-zinc-300">
                          {ref.referrer_id.slice(0, 6)}...{ref.referrer_id.slice(-4)}
                        </td>
                        <td className="py-2 text-xs font-mono text-zinc-300">
                          {ref.referred_id.slice(0, 6)}...{ref.referred_id.slice(-4)}
                        </td>
                        <td className="py-2">
                          <span className={`text-xs px-1.5 py-0.5 ${
                            ref.level === 1
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            L{ref.level}
                          </span>
                        </td>
                        <td className="py-2">
                          <span className={`text-xs px-1.5 py-0.5 ${
                            ref.status === 'converted'
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {ref.status}
                          </span>
                        </td>
                        <td className="py-2 text-xs text-zinc-500">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-zinc-500">No referrals yet</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
